// Host-side browser Notification + sound alert helper.
// Fires OS-style popups even when the browser tab is minimized or in background.

let audioCtx = null;
let permissionRequested = false;

/**
 * Ask for browser notification permission once.
 * Returns the permission string: "granted" | "denied" | "default".
 */
export async function requestNotificationPermission() {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  if (permissionRequested) return Notification.permission;
  permissionRequested = true;
  try {
    const p = await Notification.requestPermission();
    return p;
  } catch {
    return Notification.permission;
  }
}

/**
 * Play a short "bell-like" alert tone using WebAudio.
 * No external audio file needed; works across browsers.
 */
export function playAlertSound() {
  try {
    if (!audioCtx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      audioCtx = new Ctx();
    }
    // Resume context if it was suspended by browser autoplay rules
    if (audioCtx.state === 'suspended') audioCtx.resume().catch(() => {});

    const now = audioCtx.currentTime;
    // Two-tone chime: high → low
    const tones = [
      { freq: 880, start: 0.0, dur: 0.18 },
      { freq: 660, start: 0.22, dur: 0.28 },
    ];
    tones.forEach(({ freq, start, dur }) => {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + start);
      gain.gain.exponentialRampToValueAtTime(0.35, now + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      osc.connect(gain).connect(audioCtx.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.02);
    });
  } catch {
    // ignore
  }
}

/**
 * Show a browser-level notification and play sound.
 * Clicking the notification focuses the app window.
 */
export function showAlert({ title, body, tag, icon }) {
  // Always try to play sound (works even without notification permission)
  playAlertSound();

  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;

  try {
    const n = new Notification(title, {
      body: body || '',
      tag: tag || 'olafe-alert', // dedupe same-tag notifications
      renotify: true,
      icon: icon || '/favicon.ico',
      silent: false,
    });
    n.onclick = () => {
      try {
        window.focus();
        n.close();
      } catch {}
    };
  } catch {
    // ignore
  }
}
